import { useEffect, useState, useContext } from 'react';
import { toast } from 'react-toastify';
import { Navigate, useNavigate } from 'react-router-dom';
import useApi from '../../../hooks/useApi';
import Modal from '../../../components/Modal';
import FormInput from '../../../components/FormInput';
import Table from '../../../components/Table';
import Select from 'react-select';
import { FaEdit, FaTrash, FaEye, FaDownload, FaUser, FaTimes } from 'react-icons/fa';
import { AuthContext } from '../../../context/AuthContext';
import { Country, State, City } from 'country-state-city';
import apiService from '../../../services/apiService';

function FarmerWasteManagement() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useContext(AuthContext);
  const { data: requests, loading: requestsLoading, error: requestsError, fetchData: fetchRequests } = useApi(`/waste/farmer/${user?.id || ''}`);
  const { data: collectors, loading: collectorsLoading, error: collectorsLoadingError } = useApi('/users/userRole/waste_collector');
  const { data: bids, loading: bidsLoading, error: bidsError, fetchData: fetchBids } = useApi(null);
  const { data: collectorDetailsData, loading: collectorDetailsLoading, error: collectorDetailsError, fetchData: fetchCollectorDetails } = useApi(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [viewingRequest, setViewingRequest] = useState(null);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [collectorDetails, setCollectorDetails] = useState(null);
  const [form, setForm] = useState({
    title: '', // Added title field
    quantity: '',
    unit: 'kg',
    type: '',
    location: {
      city: '',
      postalCode: '',
      country: '',
      state: '',
    },
    description: '',
    deadline: '',
    estimatedValuePerUnit: '',
    totalEstimatedValue: '',
    attachments: [],
  });
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    setIsLoading(requestsLoading || collectorsLoading);
    setCountries(Country.getAllCountries().map((country) => ({
      value: country.isoCode,
      label: country.name,
    })));
  }, [requestsLoading, collectorsLoading]);

  useEffect(() => {
    if (bids) console.log('Bids data:', bids);
  }, [bids]);

  useEffect(() => {
    if (collectorDetailsData) setCollectorDetails(collectorDetailsData);
  }, [collectorDetailsData]);

  useEffect(() => {
    if (form.location.country) {
      setStates(State.getStatesOfCountry(form.location.country).map((state) => ({
        value: state.isoCode,
        label: state.name,
      })));
      setCities([]);
    } else {
      setStates([]);
      setCities([]);
    }
  }, [form.location.country]);

  useEffect(() => {
    if (form.location.state && form.location.country) {
      setCities(City.getCitiesOfState(form.location.country, form.location.state).map((city) => ({
        value: city.name,
        label: city.name,
      })));
    } else {
      setCities([]);
    }
  }, [form.location.state, form.location.country]);

  useEffect(() => {
    const calculateTotal = () => {
      let quantityInKg = Number(form.quantity) || 0;
      if (form.unit === 'ton') {
        quantityInKg *= 1000;
      }
      const estimatedValuePerUnit = Number(form.estimatedValuePerUnit) || 0;
      const total = quantityInKg * estimatedValuePerUnit;
      setForm((prevState) => ({
        ...prevState,
        totalEstimatedValue: total.toFixed(2),
      }));
    };
    calculateTotal();
  }, [form.quantity, form.unit, form.estimatedValuePerUnit]);

  if (!isAuthenticated() || user?.role !== 'farmer') {
    toast.error('Access denied. Farmers only.');
    return <Navigate to="/login" />;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('location.')) {
      const field = name.split('.')[1];
      setForm((prevState) => ({
        ...prevState,
        location: { ...prevState.location, [field]: value },
      }));
    } else {
      setForm((prevState) => ({ ...prevState, [name]: value }));
    }
  };

  const handleSelectChange = (name) => (selected) => {
    if (name === 'location.country') {
      setForm((prevState) => ({
        ...prevState,
        location: { ...prevState.location, country: selected ? selected.value : '', state: '', city: '' },
      }));
    } else if (name === 'location.state') {
      setForm((prevState) => ({
        ...prevState,
        location: { ...prevState.location, state: selected ? selected.value : '', city: '' },
      }));
    } else if (name === 'location.city') {
      setForm((prevState) => ({
        ...prevState,
        location: { ...prevState.location, city: selected ? selected.value : '' },
      }));
    } else if (name === 'unit') {
      setForm((prevState) => ({ ...prevState, unit: selected ? selected.value : 'kg' }));
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) {
      toast.error('No files selected');
      return;
    }
    const formData = new FormData();
    files.forEach((file) => formData.append('attachments', file));
    try {
      const response = await apiService.uploadFiles('/waste/upload', formData, { timeout: 30000 });
      if (response && response.urls) {
        const normalizedUrls = response.urls.map((url) => (url.startsWith('/') ? url : `/${url}`));
        setForm((prevState) => ({
          ...prevState,
          attachments: [...prevState.attachments, ...normalizedUrls],
        }));
        toast.success('Files uploaded successfully');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Upload failed:', err.response?.config, err.message);
      toast.error(err.response?.data?.error || 'Failed to upload attachments');
    }
  };

  const handleEdit = (request) => {
    setEditingRequest(request);
    setForm({
      title: request.title || '', // Added title field
      quantity: request.quantity || '',
      unit: request.unit || 'kg',
      type: request.type || '',
      location: {
        city: request.location?.city || '',
        postalCode: request.location?.postalCode || '',
        country: request.location?.country || '',
        state: request.location?.state || '',
      },
      description: request.description || '',
      deadline: request.deadline ? new Date(request.deadline).toISOString().split('T')[0] : '',
      estimatedValuePerUnit: request.estimatedValuePerUnit || '',
      totalEstimatedValue: request.totalEstimatedValue || '',
      attachments: request.attachments || [],
    });
    setIsCreateMode(false);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setForm({
      title: '', // Added title field
      quantity: '',
      unit: 'kg',
      type: '',
      location: {
        city: '',
        postalCode: '',
        country: '',
        state: '',
      },
      description: '',
      deadline: '',
      estimatedValuePerUnit: '',
      totalEstimatedValue: '',
      attachments: [],
    });
    setIsCreateMode(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (requestId) => {
    if (window.confirm('Are you sure you want to delete this waste request?')) {
      try {
        await apiService.delete(`/waste/${requestId}`);
        toast.success('Waste request deleted successfully');
        fetchRequests();
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to delete waste request');
      }
    }
  };

  const handleViewBids = async (request) => {
    try {
      console.log('Navigating to bids for request ID:', request._id);
      navigate(`/farmer/bids/${request._id}`);
    } catch (err) {
      console.error('handleViewBids error:', err);
    }
  };

  const handleViewCollector = async (collectorId) => {
    if (!collectorId) {
      toast.error('Invalid collector ID');
      return;
    }
    const endpoint = `/waste/collector-details/${collectorId}`;
    console.log('Calling fetchCollectorDetails with:', { endpoint, collectorId });
    try {
      await fetchCollectorDetails(endpoint);
      setIsDetailsModalOpen(true);
    } catch (err) {
      console.error('handleViewCollector error:', err);
    }
  };

  const handleAcceptBid = async (requestId, bidId) => {
    try {
      await apiService.put(`/waste/bid/status/${requestId}/${bidId}`, { status: 'accepted' });
      toast.success('Bid accepted successfully');
      fetchBids(`/waste/bids/${requestId}`);
    } catch (err) {
      console.error('Accept bid error:', err);
      toast.error(err.response?.data?.error || 'Failed to accept bid');
    }
  };

  const handleDeclineBid = async (requestId, bidId) => {
    try {
      await apiService.put(`/waste/bid/status/${requestId}/${bidId}`, { status: 'declined' });
      toast.success('Bid declined successfully');
      fetchBids(`/waste/bids/${requestId}`);
    } catch (err) {
      console.error('Decline bid error:', err);
      toast.error(err.response?.data?.error || 'Failed to decline bid');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isCreateMode && (!form.title || !form.quantity || !form.unit || !form.type || !form.description || !form.deadline)) {
      toast.error('Please fill in all required fields (title, quantity, unit, type, description, deadline)');
      return;
    }
    if (e.target.querySelector('input[type="file"]').files.length > 0 && form.attachments.length === 0) {
      toast.error('Please wait for file uploads to complete');
      return;
    }
    try {
      let quantityInKg = Number(form.quantity);
      if (form.unit === 'ton') {
        quantityInKg *= 1000;
      }
      if (isCreateMode) {
        await apiService.post('/waste', {
          farmerId: user.id,
          title: form.title, // Added title field
          quantity: quantityInKg,
          unit: form.unit,
          type: form.type,
          location: form.location,
          description: form.description,
          deadline: new Date(form.deadline),
          estimatedValuePerUnit: form.estimatedValuePerUnit ? Number(form.estimatedValuePerUnit) : 0,
          totalEstimatedValue: Number(form.totalEstimatedValue) || 0,
          attachments: form.attachments,
          status: 'bidding',
        });
        toast.success('Waste request created successfully');
      } else {
        await apiService.put(`/waste/${editingRequest._id}`, {
          title: form.title, // Added title field
          quantity: quantityInKg,
          unit: form.unit,
          type: form.type,
          location: form.location,
          description: form.description,
          deadline: new Date(form.deadline),
          estimatedValuePerUnit: form.estimatedValuePerUnit ? Number(form.estimatedValuePerUnit) : undefined,
          totalEstimatedValue: Number(form.totalEstimatedValue) || undefined,
          attachments: form.attachments,
        });
        toast.success('Waste request updated successfully');
      }
      fetchRequests();
      setIsModalOpen(false);
      setEditingRequest(null);
      setIsCreateMode(false);
      setForm({
        title: '', // Added title field
        quantity: '',
        unit: 'kg',
        type: '',
        location: {
          city: '',
          postalCode: '',
          country: '',
          state: '',
        },
        description: '',
        deadline: '',
        estimatedValuePerUnit: '',
        totalEstimatedValue: '',
        attachments: [],
      });
    } catch (err) {
      console.error('Error saving waste request:', err.response?.data);
      toast.error(err.response?.data?.error || 'Failed to save waste request');
    }
  };

  const handleViewRequest = (request) => {
    setViewingRequest(request);
    setIsViewModalOpen(true);
  };

  const handleViewAttachment = (attachmentUrl) => {
    setSelectedAttachment(attachmentUrl);
    setIsAttachmentModalOpen(true);
  };

  const handleDownloadAttachment = async (attachmentUrl) => {
    try {
      const { blob, contentType } = await apiService.getBlob(attachmentUrl);
      const url = window.URL.createObjectURL(new Blob([blob], { type: contentType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachmentUrl.split('/').pop());
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download attachment');
    }
  };

  const handleRemoveAttachment = (attachmentToRemove) => {
    setForm((prevState) => ({
      ...prevState,
      attachments: prevState.attachments.filter((attachment) => attachment !== attachmentToRemove),
    }));
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = requests?.slice(indexOfFirstItem, indexOfLastItem) || [];
  const totalPages = Math.ceil((requests?.length || 0) / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const columns = [
    { key: 'title', label: 'Title' }, // Added title column
    { key: 'type', label: 'Type' },
    { key: 'quantity', label: 'Quantity', render: (r) => `${r.quantity} ${r.unit || 'kg'}` },
    { key: 'location.country', label: 'Country', render: (r) => r.location?.country || 'N/A' },
    { key: 'deadline', label: 'Deadline', render: (r) => r.deadline ? new Date(r.deadline).toLocaleDateString() : 'N/A' },
    { key: 'estimatedValuePerUnit', label: 'Est. Value/Unit ($)', render: (r) => r.estimatedValuePerUnit || 'N/A' },
    { key: 'totalEstimatedValue', label: 'Total Est. Value ($)', render: (r) => r.totalEstimatedValue || 'N/A' },
    {
      key: 'attachments',
      label: 'Attachments',
      render: (r) => (
        r.attachments?.length > 0 ? (
          <button
            onClick={() => handleViewAttachment(r.attachments[0])}
            className="text-blue-500 hover:text-blue-700"
            title="View Attachment"
          >
            <FaEye />
          </button>
        ) : 'No'
      ),
    },
    { key: 'collectorId', label: 'Collector', render: (r) => `${r.collectorId?.firstName || ''} ${r.collectorId?.lastName || ''}` || 'Unassigned' },
    { key: 'status', label: 'Status' },
    {
      key: 'bids',
      label: 'Bids',
      render: (r) => (
        <button
          onClick={() => handleViewBids(r)}
          className="text-blue-500 hover:text-blue-700"
          title="View Bids"
          aria-label="View bids"
        >
          <FaEye /> ({r.bids?.length || 0})
        </button>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <div className="flex space-x-2">
          <button onClick={() => handleEdit(r)} className="text-yellow-500 hover:text-yellow-700" title="Edit"><FaEdit /></button>
          <button onClick={() => handleDelete(r._id)} className="text-red-500 hover:text-red-700" title="Delete"><FaTrash /></button>
          <button onClick={() => handleViewRequest(r)} className="text-blue-500 hover:text-blue-700" title="View"><FaEye /></button>
        </div>
      ),
    },
  ];

  const typeOptions = [
  { value: 'fertilizer', label: 'Fertilizer' },
  { value: 'feed', label: 'Feed' },
  { value: 'energy', label: 'Energy' },
  { value: 'compost', label: 'Compost' },
  { value: 'biogas', label: 'Biogas' },
  { value: 'biomass', label: 'Biomass' },
  { value: 'recycling', label: 'Recycling' },
  { value: 'organic_waste', label: 'Organic Waste' },
  { value: 'poultry_litter', label: 'Poultry Litter' },
  { value: 'soil_conditioner', label: 'Soil Conditioner' },
  { value: 'manure_processing', label: 'Manure Processing' },
  { value: 'waste_to_energy', label: 'Waste to Energy' },
  { value: 'agricultural_reuse', label: 'Agricultural Reuse' },
  { value: 'feather_recycling', label: 'Feather Recycling' },
  { value: 'bedding_waste', label: 'Bedding Waste' },
  { value: 'industrial_recycling', label: 'Industrial Recycling' },
  { value: 'sustainable_disposal', label: 'Sustainable Disposal' },
];

  const unitOptions = [
    { value: 'kg', label: 'kg' },
    { value: 'ton', label: 'ton' },
  ];

  const renderAttachment = (attachmentUrl) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
    const fullUrl = attachmentUrl.startsWith('http')
      ? attachmentUrl
      : `${baseUrl}/${attachmentUrl.replace(/^\/+/, '')}`;
    const ext = fullUrl.toLowerCase().split('.').pop();

    if (['jpg', 'jpeg', 'png'].includes(ext)) {
      return (
        <img
          src={fullUrl}
          alt="Attachment"
          className="w-full h-auto max-h-64 object-contain rounded-lg"
        />
      );
    } else if (ext === 'pdf') {
      return (
        <iframe
          src={`${fullUrl}#toolbar=0&navpanes=0`}
          title="Attachment Preview"
          className="w-full h-64 border rounded-lg"
          style={{ border: 'none' }}
        />
      );
    }

    return (
      <p className="text-gray-600 dark:text-gray-400">
        Unsupported file type
      </p>
    );
  };

  return (
    <div className="p-5 dark:bg-gray-900 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">My Waste Management</h2>
        <button
          onClick={handleCreate}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200 shadow-md"
        >
          Create Request
        </button>
      </div>
      {isLoading ? (
        <div className="card text-center text-gray-600 dark:text-gray-300 p-5 rounded-lg shadow-md">Loading...</div>
      ) : requestsError || collectorsLoadingError ? (
        <div className="card text-center text-red-600 dark:text-red-400 p-5 rounded-lg shadow-md">Error: {requestsError || collectorsLoadingError}</div>
      ) : !requests || requests.length === 0 ? (
        <div className="card text-center text-gray-600 dark:text-gray-300 p-5 rounded-lg shadow-md">No waste requests found.</div>
      ) : (
        <div className="card bg-white dark:bg-gray-800 p-5 rounded-lg shadow-md">
          <Table columns={columns} data={currentItems} />
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-4">
              <div className="text-sm text-gray-600 dark:text-gray-300">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, requests.length)} of {requests.length} entries
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md disabled:opacity-50"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => paginate(page)}
                    className={`px-3 py-1 rounded-md ${
                      currentPage === page
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingRequest(null);
          setIsCreateMode(false);
          setForm({
            title: '', // Added title field
            quantity: '',
            unit: 'kg',
            type: '',
            location: {
              city: '',
              postalCode: '',
              country: '',
              state: '',
            },
            description: '',
            deadline: '',
            estimatedValuePerUnit: '',
            totalEstimatedValue: '',
            attachments: [],
          });
        }}
        title={isCreateMode ? 'Create Waste Request' : 'Update Waste Request'}
        className="max-w-4xl mx-4"
      >
        <div className="max-h-[80vh] overflow-y-auto p-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <FormInput
                  label="Title"
                  name="title"
                  type="text"
                  value={form.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter request title"
                  className="w-full p-2 border rounded-lg dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Quantity & Unit</label>
                <div className="flex items-center space-x-2">
                  <FormInput
                    name="quantity"
                    type="number"
                    value={form.quantity}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter quantity"
                    className="w-full p-2 border rounded-lg dark:bg-gray-800 dark:text-white"
                  />
                  <Select
                    options={unitOptions}
                    value={unitOptions.find((u) => u.value === form.unit)}
                    onChange={handleSelectChange('unit')}
                    placeholder="Unit"
                    className="w-24"
                    classNamePrefix="react-select"
                  />
                </div>
              </div>
              <div>
                <FormInput
                  label="Type"
                  name="type"
                  type="select"
                  value={form.type}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border rounded-lg dark:bg-gray-800 dark:text-white"
                >
                  <option value="">Select type</option>
                  {typeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </FormInput>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Country</label>
                <Select
                  options={countries}
                  value={countries.find((c) => c.value === form.location.country)}
                  onChange={handleSelectChange('location.country')}
                  placeholder="Select country"
                  className="w-full"
                  classNamePrefix="react-select"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">State</label>
                <Select
                  options={states}
                  value={states.find((s) => s.value === form.location.state)}
                  onChange={handleSelectChange('location.state')}
                  placeholder="Select state"
                  className="w-full"
                  isDisabled={!form.location.country}
                  classNamePrefix="react-select"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">City</label>
                <Select
                  options={cities}
                  value={cities.find((c) => c.value === form.location.city)}
                  onChange={handleSelectChange('location.city')}
                  placeholder="Select city"
                  className="w-full"
                  isDisabled={!form.location.state}
                  classNamePrefix="react-select"
                />
              </div>
              <div>
                <FormInput
                  label="Postal Code"
                  name="location.postalCode"
                  type="text"
                  value={form.location.postalCode}
                  onChange={handleInputChange}
                  placeholder="Enter postal code"
                  className="w-full p-2 border rounded-lg dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <FormInput
                  label="Description"
                  name="description"
                  type="text"
                  value={form.description}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter description"
                  className="w-full p-2 border rounded-lg dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <FormInput
                  label="Deadline"
                  name="deadline"
                  type="date"
                  value={form.deadline}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border rounded-lg dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <FormInput
                  label={`Estimated Value per ${form.unit} ($)`}
                  name="estimatedValuePerUnit"
                  type="number"
                  value={form.estimatedValuePerUnit}
                  onChange={handleInputChange}
                  placeholder={`Enter value per ${form.unit}`}
                  className="w-full p-2 border rounded-lg dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <FormInput
                  label="Total Estimated Value ($)"
                  name="totalEstimatedValue"
                  type="text"
                  value={form.totalEstimatedValue}
                  readOnly
                  className="w-full p-2 border rounded-lg bg-gray-100 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Attachments</label>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="w-full p-2 border rounded-lg dark:bg-gray-800 dark:text-white"
                />
                {form.attachments.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {form.attachments.map((attachment, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-1 rounded-md text-sm"
                      >
                        {attachment.split('/').pop()}
                        <button
                          onClick={() => handleRemoveAttachment(attachment)}
                          className="ml-2 text-red-500 hover:text-red-700"
                          title="Remove Attachment"
                        >
                          <FaTimes />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-4 mt-4">
              <button
                type="button"
                onClick={() => {
                  setIsModalOpen(false);
                  setEditingRequest(null);
                  setIsCreateMode(false);
                  setForm({
                    title: '', // Added title field
                    quantity: '',
                    unit: 'kg',
                    type: '',
                    location: {
                      city: '',
                      postalCode: '',
                      country: '',
                      state: '',
                    },
                    description: '',
                    deadline: '',
                    estimatedValuePerUnit: '',
                    totalEstimatedValue: '',
                    attachments: [],
                  });
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                disabled={isLoading}
              >
                {isCreateMode ? 'Create' : 'Update'}
              </button>
            </div>
          </form>
        </div>
      </Modal>
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setViewingRequest(null);
        }}
        title={`Request Details #${viewingRequest?._id}`}
        className="max-w-full lg:max-w-3xl mx-auto"
      >
        <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          {viewingRequest ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                  <p className="text-base text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                    {viewingRequest.title || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                  <p className="text-base text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                    {viewingRequest.type || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Quantity</label>
                  <p className="text-base text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                    {viewingRequest.quantity} {viewingRequest.unit || 'kg'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Country</label>
                  <p className="text-base text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                    {viewingRequest.location?.country || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">State</label>
                  <p className="text-base text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                    {viewingRequest.location?.state || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">City</label>
                  <p className="text-base text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                    {viewingRequest.location?.city || 'N/A'}
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Postal Code</label>
                  <p className="text-base text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                    {viewingRequest.location?.postalCode || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                  <p className="text-base text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                    {viewingRequest.description || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Deadline</label>
                  <p className="text-base text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                    {viewingRequest.deadline ? new Date(viewingRequest.deadline).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Estimated Value per {viewingRequest.unit || 'kg'} ($)</label>
                  <p className="text-base text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                    {viewingRequest.estimatedValuePerUnit || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Total Estimated Value ($)</label>
                  <p className="text-base text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                    {viewingRequest.totalEstimatedValue || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Collector</label>
                  <p className="text-base text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                    {`${viewingRequest.collectorId?.firstName || ''} ${viewingRequest.collectorId?.lastName || ''}` || 'Unassigned'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                  <p className="text-base text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                    {viewingRequest.status || 'N/A'}
                  </p>
                </div>
              </div>
              <div className="mt-4 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Attachments</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {viewingRequest.attachments?.length > 0 ? (
                    viewingRequest.attachments.map((attachment, index) => (
                      <div key={index} className="relative">
                        {attachment.toLowerCase().endsWith('.jpg') || attachment.toLowerCase().endsWith('.jpeg') || attachment.toLowerCase().endsWith('.png') ? (
                          <img
                            src={attachment}
                            alt={`Attachment ${index + 1}`}
                            className="w-16 h-16 object-cover rounded-md border border-gray-300 dark:border-gray-600 cursor-pointer"
                            onClick={() => handleViewAttachment(attachment)}
                          />
                        ) : (
                          <span className="inline-flex items-center bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-1 rounded-md text-sm">
                            {attachment.split('/').pop()}
                          </span>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400 text-sm">None</p>
                  )}
                </div>
              </div>
              <div className="mt-4 md:col-span-2 text-xs text-gray-600 dark:text-gray-400">
                <p><strong>Created At:</strong> {new Date(viewingRequest.createdAt).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' })}</p>
                <p><strong>Updated At:</strong> {new Date(viewingRequest.updatedAt).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' })}</p>
              </div>
            </div>
          ) : (
            <p className="text-gray-600 dark:text-gray-300 text-sm">No details available.</p>
          )}
          <div className="flex justify-end mt-6">
            <button
              onClick={() => setIsViewModalOpen(false)}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={isAttachmentModalOpen}
        onClose={() => {
          setIsAttachmentModalOpen(false);
          setSelectedAttachment(null);
        }}
        title="View Attachment"
        className="max-w-2xl mx-4"
      >
        <div className="p-4 max-h-[70vh] overflow-y-auto">
          {selectedAttachment ? (
            <div className="space-y-4 text-gray-900 dark:text-gray-100">
              {renderAttachment(selectedAttachment)}
              <p className="font-medium text-sm"><strong>File:</strong> {selectedAttachment.split('/').pop()}</p>
              <button
                onClick={() => handleDownloadAttachment(selectedAttachment)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200"
              >
                <FaDownload className="inline mr-1" /> Download
              </button>
            </div>
          ) : (
            <p className="text-gray-600 dark:text-gray-300 text-sm">No attachment selected.</p>
          )}
          <div className="flex justify-end mt-4">
            <button
              onClick={() => setIsAttachmentModalOpen(false)}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setCollectorDetails(null);
        }}
        title="Collector Details"
        className="max-w-lg mx-4"
      >
        <div className="p-4">
          {collectorDetailsLoading ? (
            <p className="text-gray-600 dark:text-gray-300 text-sm">Loading collector details...</p>
          ) : collectorDetailsError ? (
            <p className="text-red-600 dark:text-red-400 text-sm">Error: {collectorDetailsError}</p>
          ) : collectorDetails ? (
            <div className="space-y-4 text-gray-900 dark:text-gray-100">
              <p className="font-medium text-sm"><strong>Name:</strong> {collectorDetails.firstName} {collectorDetails.lastName}</p>
              <p className="font-medium text-sm"><strong>Email:</strong> {collectorDetails.email}</p>
              <p className="font-medium text-sm"><strong>Phone:</strong> {collectorDetails.phone}</p>
              <p className="font-medium text-sm"><strong>Address:</strong> {collectorDetails.address}</p>
              <p className="font-medium text-sm"><strong>Street:</strong> {collectorDetails.street}</p>
              <p className="font-medium text-sm"><strong>Postal Code:</strong> {collectorDetails.postalCode}</p>
            </div>
          ) : (
            <p className="text-gray-600 dark:text-gray-300 text-sm">No collector details available.</p>
          )}
          <div className="flex justify-end mt-4">
            <button
              onClick={() => {
                setIsDetailsModalOpen(false);
                setCollectorDetails(null);
              }}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default FarmerWasteManagement;